import { Link } from "react-router-dom";
import { IMG, PURE_SOULS_URL } from "@/lib/site";
import SectionHeader from "@/components/site/SectionHeader";
import WaterVsFlameTool from "@/components/site/WaterVsFlameTool";
import Reveal from "@/components/site/Reveal";
import Seo from "@/components/site/Seo";
import { PAGE_SEO, breadcrumbLd } from "@/lib/seo";

const benefits = [
  {
    title: "Environmental",
    body: "A measurable reduction in carbon emissions, energy use, and atmospheric pollutants. A choice that quietly aligns with the values of environmentally conscious families.",
  },
  {
    title: "Emotional",
    body: "Many families describe water cremation as feeling gentler and more in keeping with how they want to remember their pet: slow, soft, and natural.",
  },
  {
    title: "Practical",
    body: "Individual aqua cremation is the standard at reputable providers, with identification and tracking used throughout the entire process.",
  },
  {
    title: "Memorial",
    body: "The clean, light remains returned are well suited to memorial keepsakes, urns, or natural scattering rituals.",
  },
];

export default function Benefits() {
  return (
    <div data-testid="benefits-page">
      <Seo
        {...PAGE_SEO.benefits}
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Benefits", path: "/benefits" },
        ])}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.texSage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#F9F9F6]/85" />
        </div>
        <div className="container-editorial pt-20 pb-16">
          <div className="overline mb-5">Comparison & Benefits</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-forest-900 max-w-4xl">
            Benefits of <span className="italic text-sage-600">water cremation</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-forest-700 leading-[1.65]">
            A balanced, honest look at the environmental and emotional benefits
            many families consider when exploring water based pet aftercare.
          </p>
        </div>
      </section>

      <section className="container-editorial pb-20">
        <SectionHeader
          overline="Interactive Comparison"
          title="Water cremation vs flame cremation"
          intro="Both options return mineral remains to the family. Choose an aspect below to explore how the journey, the footprint, and the feel compare."
        />
        <WaterVsFlameTool />
      </section>

      <section className="section-padding bg-sand-100">
        <div className="container-editorial">
          <SectionHeader
            overline="Four Quiet Benefits"
            title="The benefits families speak about most"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={Math.min(i * 0.08, 0.4)}>
              <div
                className="card-elevated card-hover rounded-2xl p-8 h-full"
                data-testid={`benefit-detail-${b.title.toLowerCase()}`}
              >
                <div className="overline mb-3">{b.title}</div>
                <p className="font-serif text-2xl text-forest-900 leading-tight tracking-tight mb-3">
                  {b.title} considerations
                </p>
                <p className="text-forest-700 leading-relaxed">{b.body}</p>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-editorial max-w-3xl">
          <p className="font-serif text-3xl sm:text-4xl text-forest-900 leading-tight tracking-tight">
            &ldquo;It felt like the choice that matched who he was, quiet, gentle,
            and close to nature.&rdquo;
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/the-process" className="btn-secondary">Read about the process</Link>
            <a
              href={PURE_SOULS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="benefits-pure-souls-cta"
            >
              Visit Pure Souls
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
